import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sessionsAPI, trainingRequestsAPI, partnersAPI, feedbackAPI, usersAPI, taxonomyAPI, preferencesAPI } from '../services/api';
import { ColumnFilterPopover } from '../components/shared/ColumnFilterPopover';
import { ActiveFiltersBar } from '../components/shared/ActiveFiltersBar';
import { ColumnsPicker } from '../components/shared/ColumnsPicker';
import { DateRangeFilterPopover } from '../components/shared/DateRangeFilterPopover';
import { DeliveryCalendar } from '../components/shared/calendar/DeliveryCalendar';
import { sessionTrainers } from '../components/shared/calendar/calendarUtils';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Search,
  CalendarDays,
  Video,
  MapPin,
  Users,
  GraduationCap,
  LayoutGrid,
  TableIcon,
  Download,
  Star,
  MessageSquare,
  Edit2,
  Trash2,
  X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

const SESSION_TYPE_LABELS = {
  virtual: { label: 'Virtual', color: 'bg-blue-100 text-blue-800', chipColor: 'bg-blue-500', icon: Video },
  in_person: { label: 'In-Person', color: 'bg-green-100 text-green-800', chipColor: 'bg-green-500', icon: MapPin },
  hybrid: { label: 'Hybrid', color: 'bg-purple-100 text-purple-800', chipColor: 'bg-purple-500', icon: Users },
};

const SESSION_STATUS_LABELS = {
  scheduled: { label: 'Scheduled', color: 'bg-blue-100 text-blue-800' },
  in_progress: { label: 'In Progress', color: 'bg-violet-100 text-violet-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-800' },
};


const SESSION_CORE_COLUMNS = [
  { key: 'project', label: 'Project' },
  { key: 'partner', label: 'Partner' },
  { key: 'requestor', label: 'Requestor' },
  { key: 'pm', label: 'Project Manager' },
  { key: 'date', label: 'Date' },
  { key: 'trainer', label: 'Trainer' },
  { key: 'type', label: 'Type' },
  { key: 'attendees', label: 'Attendees' },
  { key: 'status', label: 'Status' },
  { key: 'invoiced_month', label: 'Invoice Month' },
  { key: 'mtm_trainer', label: 'MTM Trainer' },
  { key: 'mtm_course', label: 'MTM Course' },
  { key: 'verbatims', label: 'Verbatims' },
];

const formatInvoicedMonth = (m) => {
  if (!m) return '—';
  const [y, mo] = m.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(mo, 10) - 1]} '${y.slice(2)}`;
};

export const AllSessionsPage = ({ embedded = false } = {}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sessions, setSessions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [feedbackMap, setFeedbackMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [trainerFilter, setTrainerFilter] = useState('');
  const [viewMode, setViewMode] = useState('table');
  // Header-based column filters (multi-select checklists + date range)
  const [colFilters, setColFilters] = useState({
    project: new Set(), partner: new Set(), requestor: new Set(), pm: new Set(), type: new Set(), status: new Set(), invoicedMonth: new Set(),
  });
  const [dateRange, setDateRange] = useState({ from: '', to: '' });

  // Read trainer filter from URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const trainerParam = params.get('trainer');
    if (trainerParam) {
      setTrainerFilter(decodeURIComponent(trainerParam));
    }
  }, [location.search]);

  // Quick edit state
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [quickEditSession, setQuickEditSession] = useState(null);
  const [quickEditForm, setQuickEditForm] = useState({
    title: '',
    session_date: '',
    status: 'scheduled',
    trainer_name: '',
    trainer_email: '',
    trainer_2_name: '',
    trainer_2_email: '',
  });
  const [quickEditSaving, setQuickEditSaving] = useState(false);

  // Multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  // Column visibility + taxonomy-driven custom field columns
  const [taxonomyCategories, setTaxonomyCategories] = useState([]);
  const [visibleSessionCols, setVisibleSessionCols] = useState(SESSION_CORE_COLUMNS.map(c => c.key));
  const customSessionColumns = taxonomyCategories.map(c => ({ key: `custom:${c.id}`, taxonomyKey: c.id, label: c.label }));

  useEffect(() => {
    taxonomyAPI.getAll().then(setTaxonomyCategories).catch(() => {});
    preferencesAPI.get().then(prefs => {
      if (Array.isArray(prefs.all_sessions_columns) && prefs.all_sessions_columns.length) {
        setVisibleSessionCols(prefs.all_sessions_columns);
      }
    }).catch(() => {});
  }, []);

  const toggleSessionCol = (key) => {
    setVisibleSessionCols(prev => {
      const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
      preferencesAPI.set('all_sessions_columns', next).catch(() => {});
      return next;
    });
  };
  const isSessionColVisible = (key) => visibleSessionCols.includes(key);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      // Cross-program view: sessions can belong to Private Deliveries, Partner
      // Conferences, or Regional In-Person projects — fetch all 3 project types.
      const [sessionsData, privateProjects, conferenceProjects, rioProjects, partnersData, usersData, feedbackData] = await Promise.all([
        sessionsAPI.getAll(),
        trainingRequestsAPI.getAll({ project_type: 'private_deliveries', limit: 10000 }),
        trainingRequestsAPI.getAll({ project_type: 'partner_conferences', limit: 10000 }),
        trainingRequestsAPI.getAll({ project_type: 'regional_in_person', limit: 10000 }),
        partnersAPI.getAll(),
        usersAPI.getAll().catch(() => []),
        feedbackAPI.getAllSessionFeedback().catch(() => []),
      ]);
      setSessions(sessionsData);
      setProjects([...privateProjects, ...conferenceProjects, ...rioProjects]);
      setPartners(partnersData);
      setUsers(usersData);
      const map = {};
      feedbackData.forEach(f => { map[f.session_id] = f; });
      setFeedbackMap(map);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openQuickEdit = (session, e) => {
    e.stopPropagation();
    setQuickEditSession(session);
    setQuickEditForm({
      title: session.title || '',
      session_date: session.session_date || '',
      status: session.status || 'scheduled',
      trainer_name: session.trainer_name || '',
      trainer_email: session.trainer_email || '',
      trainer_2_name: session.trainer_2_name || '',
      trainer_2_email: session.trainer_2_email || '',
    });
    setQuickEditOpen(true);
  };

  const handleQuickEditSave = async () => {
    if (!quickEditSession) return;
    setQuickEditSaving(true);
    try {
      await sessionsAPI.update(quickEditSession.id, quickEditForm);
      toast.success('Session updated');
      setQuickEditOpen(false);
      loadData();
    } catch (err) {
      toast.error('Failed to update session');
    } finally {
      setQuickEditSaving(false);
    }
  };

  const toggleSelectSession = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === sortedSessions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(sortedSessions.map(s => s.id)));
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.size === 0) return;
    setBulkSaving(true);
    try {
      await Promise.all([...selectedIds].map(id => sessionsAPI.update(id, { status: bulkStatus })));
      toast.success(`Updated ${selectedIds.size} session${selectedIds.size > 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      setBulkEditOpen(false);
      loadData();
    } catch {
      toast.error('Failed to update sessions');
    } finally {
      setBulkSaving(false);
    }
  };

  const openDeleteConfirm = (session, e) => {
    e.stopPropagation();
    setSessionToDelete(session);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    try {
      await sessionsAPI.delete(sessionToDelete.id);
      toast.success('Session deleted');
      setSelectedIds(prev => { const n = new Set(prev); n.delete(sessionToDelete.id); return n; });
      setDeleteConfirmOpen(false);
      setSessionToDelete(null);
      loadData();
    } catch {
      toast.error('Failed to delete session');
    }
  };

  const getProjectName = (projectId) =>
    projects.find(p => p.id === projectId)?.title || '—';

  const getPartnerCode = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return '—';
    return partners.find(p => p.id === project.partner_id)?.code || '—';
  };

  const getPartnerName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return '—';
    return partners.find(p => p.id === project.partner_id)?.name || '—';
  };

  const getRequestorName = (projectId) =>
    projects.find(p => p.id === projectId)?.requestor_name || '—';

  const getPMName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return '—';
    if (project.pm_name) return project.pm_name;
    const pm = users.find(u => u.id === project.pm_id);
    return pm ? pm.name : '—';
  };

  const getFeedback = (sessionId) => feedbackMap[sessionId] || {};

  // Unique option lists for each column's filter popover
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const projectFilterOptions = useMemo(() => [...new Set(sessions.map(s => getProjectName(s.training_project_id)))].filter(v => v !== '—').sort()
    .map(v => ({ value: v, label: v })), [sessions, projects]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const partnerFilterOptions = useMemo(() => [...new Set(sessions.map(s => getPartnerName(s.training_project_id)))].filter(v => v !== '—').sort()
    .map(v => ({ value: v, label: v })), [sessions, projects, partners]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const requestorFilterOptions = useMemo(() => [...new Set(sessions.map(s => getRequestorName(s.training_project_id)))].filter(v => v !== '—').sort()
    .map(v => ({ value: v, label: v })), [sessions, projects]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pmFilterOptions = useMemo(() => [...new Set(sessions.map(s => getPMName(s.training_project_id)))].filter(v => v !== '—').sort()
    .map(v => ({ value: v, label: v })), [sessions, projects, users]);
  const typeFilterOptions = Object.entries(SESSION_TYPE_LABELS).map(([key, { label }]) => ({ value: key, label }));
  const statusFilterOptions = Object.entries(SESSION_STATUS_LABELS).map(([key, { label }]) => ({ value: key, label }));
  const invoicedMonthFilterOptions = [...new Set(sessions.map(s => s.invoiced_month).filter(Boolean))].sort()
    .map(v => ({ value: v, label: formatInvoicedMonth(v) }));

  const setColFilter = (key, valuesSet) => setColFilters(prev => ({ ...prev, [key]: valuesSet }));

  const filteredSessions = sessions.filter(session => {
    const projectName = getProjectName(session.training_project_id).toLowerCase();
    const searchLower = search.toLowerCase();
    const matchesSearch = !search ||
      session.title?.toLowerCase().includes(searchLower) ||
      session.trainer_name?.toLowerCase().includes(searchLower) ||
      projectName.includes(searchLower);
    const matchesTrainer = !trainerFilter ||
      session.trainer_name?.toLowerCase().includes(trainerFilter.toLowerCase()) ||
      session.trainer_email?.toLowerCase().includes(trainerFilter.toLowerCase()) ||
      (session.session_trainers || []).some(t => t.name?.toLowerCase().includes(trainerFilter.toLowerCase()));
    if (!matchesSearch || !matchesTrainer) return false;
    if (colFilters.project.size > 0 && !colFilters.project.has(getProjectName(session.training_project_id))) return false;
    if (colFilters.partner.size > 0 && !colFilters.partner.has(getPartnerName(session.training_project_id))) return false;
    if (colFilters.requestor.size > 0 && !colFilters.requestor.has(getRequestorName(session.training_project_id))) return false;
    if (colFilters.pm.size > 0 && !colFilters.pm.has(getPMName(session.training_project_id))) return false;
    if (colFilters.type.size > 0 && !colFilters.type.has(session.session_type)) return false;
    if (colFilters.status.size > 0 && !colFilters.status.has(session.status)) return false;
    if (colFilters.invoicedMonth.size > 0 && !colFilters.invoicedMonth.has(session.invoiced_month)) return false;
    if (dateRange.from || dateRange.to) {
      const d = (session.session_date || '').slice(0, 10);
      if (dateRange.from && d < dateRange.from) return false;
      if (dateRange.to && d > dateRange.to) return false;
    }
    return true;
  });

  const sortedSessions = [...filteredSessions].sort(
    (a, b) => new Date(b.session_date) - new Date(a.session_date)
  );

  // ─── CSV Export ─────────────────────────────────────────────────────────────
  const handleExportCSV = () => {
    const headers = [
      'Session Title', 'Project', 'Partner', 'Date', 'Start', 'End',
      'Type', 'Status', 'Trainer', 'Trainer Email', 'Registered', 'Attended', 'Invoice Month',
      'MTM Trainer Rating', 'MTM Course Rating', 'Verbatims',
    ];
    const escape = (v) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = sortedSessions.map(s => {
      const fb = getFeedback(s.id);
      return [
        s.title, getProjectName(s.training_project_id), getPartnerCode(s.training_project_id),
        s.session_date || '', s.start_time || '', s.end_time || '',
        s.session_type || '', s.status || '', s.trainer_name || '', s.trainer_email || '',
        s.registered_count || 0, s.attended_count || 0, s.invoiced_month || '',
        fb.mtm_trainer_rating ?? '', fb.mtm_course_rating ?? '', fb.verbatims || '',
      ].map(escape).join(',');
    });
    const csv = [headers.map(escape).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sessions_${format(new Date(), 'yyyyMMdd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const SESSION_TYPE_HEX = { virtual: '#3B82F6', in_person: '#22C55E', hybrid: '#A855F7' };
  const calendarEvents = sessions.map(s => ({
    id: s.id,
    title: s.title,
    start: s.session_date,
    end: s.end_date,
    startTime: s.start_time,
    endTime: s.end_time,
    color: s.status === 'cancelled' ? '#9CA3AF' : (SESSION_TYPE_HEX[s.session_type] || SESSION_TYPE_HEX.virtual),
    muted: s.status === 'cancelled',
    statusLabel: (SESSION_STATUS_LABELS[s.status] || {}).label || s.status,
    trainers: sessionTrainers(s),
    pm: getPMName(s.training_project_id),
    client: getPartnerName(s.training_project_id),
    onClick: () => navigate(`/training-projects/${s.training_project_id}`),
  }));
  const calendarLegend = [
    ...Object.entries(SESSION_TYPE_LABELS).map(([k, v]) => ({ label: v.label, color: SESSION_TYPE_HEX[k] })),
    { label: 'Cancelled', color: '#9CA3AF' },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="all-sessions-page">
      <div className="flex items-start justify-between">
        {!embedded && (
          <div>
            <h1 className="text-3xl font-heading font-bold">All Sessions</h1>
            <p className="text-muted-foreground">View and track all training sessions across projects</p>
          </div>
        )}

        <div className={`flex items-center gap-2 ${embedded ? 'ml-auto' : ''}`}>
          {viewMode === 'table' && (
            <ColumnsPicker
              columns={[...SESSION_CORE_COLUMNS, ...customSessionColumns]}
              visibleKeys={visibleSessionCols}
              onToggle={toggleSessionCol}
              testId="all-sessions-columns"
            />
          )}
          {/* Export */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv-btn">
                Export as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => sessionsAPI.exportWithFeedback('xlsx')} data-testid="export-excel-btn">
                Export as Excel
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* View toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none border-0"
              onClick={() => setViewMode('table')}
              data-testid="view-table-btn"
            >
              <TableIcon className="w-4 h-4 mr-1" />
              Table
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'default' : 'ghost'}
              size="sm"
              className="rounded-none border-0"
              onClick={() => setViewMode('calendar')}
              data-testid="view-calendar-btn"
            >
              <LayoutGrid className="w-4 h-4 mr-1" />
              Calendar
            </Button>
          </div>
        </div>
      </div>

      {/* Filters (always visible) */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search sessions, projects, trainers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-sessions"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
            <TableIcon className="w-3 h-3" />
            Tip: click any column header below to filter by Project, Partner, Requestor, Project Manager, Date, Type or Status
          </p>
          {trainerFilter && (
            <div className="flex items-center gap-2 mt-3 text-sm" data-testid="trainer-filter-chip">
              <span className="text-muted-foreground">Filtered by trainer:</span>
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-violet-100 text-violet-800 rounded-full font-medium">
                <span>{trainerFilter}</span>
                <button
                  onClick={() => setTrainerFilter('')}
                  className="hover:text-violet-600 ml-0.5"
                  data-testid="clear-trainer-filter"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── TABLE VIEW ──────────────────────────────────────────────────────── */}
      {viewMode === 'table' && (
        <Card>
          <CardContent className="pt-6">
            {/* Bulk action bar */}
            {selectedIds.size > 0 && (
              <div className="flex items-center justify-between bg-primary/5 border border-primary/20 rounded-lg px-4 py-2 mb-4">
                <span className="text-sm font-medium text-primary">
                  {selectedIds.size} session{selectedIds.size > 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setSelectedIds(new Set())} data-testid="clear-selection-btn">
                    Clear
                  </Button>
                  <Button size="sm" onClick={() => setBulkEditOpen(true)} className="bg-lime text-lime-foreground hover:bg-lime/90" data-testid="bulk-edit-btn">
                    Bulk Status Update
                  </Button>
                </div>
              </div>
            )}
            <div className="mb-3">
              <ActiveFiltersBar
                filters={[
                  { key: 'project', label: 'Project', set: colFilters.project, options: projectFilterOptions, onChange: (s) => setColFilter('project', s) },
                  { key: 'partner', label: 'Partner', set: colFilters.partner, options: partnerFilterOptions, onChange: (s) => setColFilter('partner', s) },
                  { key: 'requestor', label: 'Requestor', set: colFilters.requestor, options: requestorFilterOptions, onChange: (s) => setColFilter('requestor', s) },
                  { key: 'pm', label: 'Project Manager', set: colFilters.pm, options: pmFilterOptions, onChange: (s) => setColFilter('pm', s) },
                  { key: 'type', label: 'Type', set: colFilters.type, options: typeFilterOptions, onChange: (s) => setColFilter('type', s) },
                  { key: 'status', label: 'Status', set: colFilters.status, options: statusFilterOptions, onChange: (s) => setColFilter('status', s) },
                  { key: 'invoicedMonth', label: 'Invoice Month', set: colFilters.invoicedMonth, options: invoicedMonthFilterOptions, onChange: (s) => setColFilter('invoicedMonth', s) },
                ]}
                search={search}
                onClearSearch={() => setSearch('')}
                testId="sessions-active-filters"
              />
            </div>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => (
                  <div key={i} className="h-14 bg-muted animate-pulse rounded" />
                ))}
              </div>
            ) : sortedSessions.length > 0 ? (
              <div className="overflow-x-auto">
                <Table data-testid="sessions-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <Checkbox
                          checked={selectedIds.size === sortedSessions.length && sortedSessions.length > 0}
                          onCheckedChange={toggleSelectAll}
                          data-testid="select-all-sessions"
                        />
                      </TableHead>
                      <TableHead>SESSION</TableHead>
                      {isSessionColVisible('project') && (
                        <TableHead>
                          <ColumnFilterPopover label="Project" options={projectFilterOptions} selected={colFilters.project} onChange={(s) => setColFilter('project', s)} />
                        </TableHead>
                      )}
                      {isSessionColVisible('partner') && (
                        <TableHead>
                          <ColumnFilterPopover label="Partner" options={partnerFilterOptions} selected={colFilters.partner} onChange={(s) => setColFilter('partner', s)} />
                        </TableHead>
                      )}
                      {isSessionColVisible('requestor') && (
                        <TableHead>
                          <ColumnFilterPopover label="Requestor" options={requestorFilterOptions} selected={colFilters.requestor} onChange={(s) => setColFilter('requestor', s)} />
                        </TableHead>
                      )}
                      {isSessionColVisible('pm') && (
                        <TableHead>
                          <ColumnFilterPopover label="Project Manager" options={pmFilterOptions} selected={colFilters.pm} onChange={(s) => setColFilter('pm', s)} />
                        </TableHead>
                      )}
                      {isSessionColVisible('date') && (
                        <TableHead>
                          <DateRangeFilterPopover label="Date" range={dateRange} onChange={setDateRange} />
                        </TableHead>
                      )}
                      {isSessionColVisible('trainer') && <TableHead>TRAINER</TableHead>}
                      {isSessionColVisible('type') && (
                        <TableHead>
                          <ColumnFilterPopover label="Type" options={typeFilterOptions} selected={colFilters.type} onChange={(s) => setColFilter('type', s)} />
                        </TableHead>
                      )}
                      {isSessionColVisible('attendees') && <TableHead>ATTENDEES</TableHead>}
                      {isSessionColVisible('status') && (
                        <TableHead>
                          <ColumnFilterPopover label="Status" options={statusFilterOptions} selected={colFilters.status} onChange={(s) => setColFilter('status', s)} />
                        </TableHead>
                      )}
                      {isSessionColVisible('invoiced_month') && (
                        <TableHead>
                          <ColumnFilterPopover label="Invoice Month" options={invoicedMonthFilterOptions} selected={colFilters.invoicedMonth} onChange={(s) => setColFilter('invoicedMonth', s)} />
                        </TableHead>
                      )}
                      {isSessionColVisible('mtm_trainer') && <TableHead>MTM TRAINER</TableHead>}
                      {isSessionColVisible('mtm_course') && <TableHead>MTM COURSE</TableHead>}
                      {isSessionColVisible('verbatims') && <TableHead>VERBATIMS</TableHead>}
                      {customSessionColumns.filter(c => isSessionColVisible(c.key)).map(c => (
                        <TableHead key={c.key}>{c.label}</TableHead>
                      ))}
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedSessions.map((session) => {
                      const typeInfo = SESSION_TYPE_LABELS[session.session_type] || SESSION_TYPE_LABELS.virtual;
                      const statusInfo = SESSION_STATUS_LABELS[session.status] || SESSION_STATUS_LABELS.scheduled;
                      const fb = getFeedback(session.id);
                      return (
                        <TableRow
                          key={session.id}
                          className={`cursor-pointer hover:bg-muted/50 ${selectedIds.has(session.id) ? 'bg-primary/5' : ''}`}
                          onClick={() => navigate(`/training-projects/${session.training_project_id}`)}
                          data-testid={`session-row-${session.id}`}
                        >
                          <TableCell onClick={(e) => { e.stopPropagation(); toggleSelectSession(session.id); }}>
                            <Checkbox
                              checked={selectedIds.has(session.id)}
                              onCheckedChange={() => toggleSelectSession(session.id)}
                              data-testid={`select-session-${session.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-start gap-2">
                              <GraduationCap className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="font-medium leading-tight">{session.title}</p>
                                {session.course_title && (
                                  <p className="text-xs text-muted-foreground">{session.course_title}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          {isSessionColVisible('project') && (
                          <TableCell>
                            <span
                              className="text-sm text-blue-600 hover:underline cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/training-projects/${session.training_project_id}`);
                              }}
                            >
                              {getProjectName(session.training_project_id)}
                            </span>
                          </TableCell>
                          )}
                          {isSessionColVisible('partner') && (
                          <TableCell>
                            <Badge variant="outline">{getPartnerCode(session.training_project_id)}</Badge>
                          </TableCell>
                          )}
                          {isSessionColVisible('requestor') && (
                          <TableCell>
                            <span className="text-sm">{getRequestorName(session.training_project_id)}</span>
                          </TableCell>
                          )}
                          {isSessionColVisible('pm') && (
                          <TableCell>
                            <span className="text-sm">{getPMName(session.training_project_id)}</span>
                          </TableCell>
                          )}
                          {isSessionColVisible('date') && (
                          <TableCell>
                            <div className="flex items-center gap-1.5 text-sm">
                              <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
                              <span>
                                {session.session_date
                                  ? format(parseISO(session.session_date), 'MMM d, yyyy')
                                  : '—'}
                              </span>
                            </div>
                            {session.is_multi_day && session.end_date && (
                              <p className="text-xs text-muted-foreground mt-0.5">
                                to {format(parseISO(session.end_date), 'MMM d')}
                              </p>
                            )}
                          </TableCell>
                          )}
                          {isSessionColVisible('trainer') && (
                          <TableCell>
                            {session.trainer_name ? (
                              <div>
                                <p className="text-sm">{session.trainer_name}</p>
                                {session.trainer_email && (
                                  <p className="text-xs text-muted-foreground">{session.trainer_email}</p>
                                )}
                                {session.trainer_2_name && (
                                  <p className="text-xs text-muted-foreground mt-0.5">+ {session.trainer_2_name}</p>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}

                          </TableCell>
                          )}
                          {isSessionColVisible('type') && (
                          <TableCell>
                            <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                          </TableCell>
                          )}
                          {isSessionColVisible('attendees') && (
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Users className="w-3.5 h-3.5 text-muted-foreground" />
                              <span>{session.attended_count || 0}</span>
                              {session.registered_count > 0 && (
                                <span className="text-muted-foreground">/ {session.registered_count}</span>
                              )}
                            </div>
                          </TableCell>
                          )}
                          {isSessionColVisible('status') && (
                          <TableCell>
                            <Badge className={statusInfo.color}>{statusInfo.label}</Badge>
                          </TableCell>
                          )}
                          {isSessionColVisible('invoiced_month') && (
                          <TableCell data-testid={`invoiced-month-${session.id}`}>
                            <span className="text-sm text-muted-foreground">{formatInvoicedMonth(session.invoiced_month)}</span>
                          </TableCell>
                          )}
                          {/* Feedback columns */}
                          {isSessionColVisible('mtm_trainer') && (
                          <TableCell data-testid={`mtm-trainer-${session.id}`}>
                            {fb.mtm_trainer_rating != null ? (
                              <div className="flex items-center gap-1">
                                <Star className="w-3.5 h-3.5 text-yellow-500" />
                                <span className="text-sm font-medium">{fb.mtm_trainer_rating}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          )}
                          {isSessionColVisible('mtm_course') && (
                          <TableCell data-testid={`mtm-course-${session.id}`}>
                            {fb.mtm_course_rating != null ? (
                              <div className="flex items-center gap-1">
                                <Star className="w-3.5 h-3.5 text-yellow-500" />
                                <span className="text-sm font-medium">{fb.mtm_course_rating}</span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          )}
                          {isSessionColVisible('verbatims') && (
                          <TableCell data-testid={`verbatims-${session.id}`}>
                            {fb.verbatims ? (
                              <div className="flex items-start gap-1 max-w-[180px]">
                                <MessageSquare className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                                <p className="text-xs text-muted-foreground truncate" title={fb.verbatims}>
                                  {fb.verbatims}
                                </p>
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-sm">—</span>
                            )}
                          </TableCell>
                          )}
                          {customSessionColumns.filter(c => isSessionColVisible(c.key)).map(c => (
                            <TableCell key={c.key}>
                              <span className="text-sm">{session.custom_fields?.[c.taxonomyKey] || '—'}</span>
                            </TableCell>
                          ))}
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={(e) => openQuickEdit(session, e)}
                                data-testid={`quick-edit-session-${session.id}`}
                                title="Quick edit"
                              >
                                <Edit2 className="w-3.5 h-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={(e) => openDeleteConfirm(session, e)}
                                data-testid={`delete-session-${session.id}`}
                                title="Delete session"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No sessions found</h3>
                <p className="text-muted-foreground">
                  {search || dateRange.from || dateRange.to || Object.values(colFilters).some(s => s.size > 0)
                    ? 'Try adjusting your filters'
                    : 'Sessions will appear here once added to training projects'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ─── CALENDAR VIEW ───────────────────────────────────────────────────── */}
      {viewMode === 'calendar' && (
        loading
          ? <div className="h-64 bg-muted animate-pulse rounded-xl" />
          : <DeliveryCalendar events={calendarEvents} legend={calendarLegend} testId="calendar-view" />
      )}

      {!loading && sortedSessions.length > 0 && viewMode === 'table' && (
        <p className="text-sm text-muted-foreground text-right">
          Showing {sortedSessions.length} of {sessions.length} session{sessions.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Quick Edit Dialog */}
      <Dialog open={quickEditOpen} onOpenChange={setQuickEditOpen}>
        <DialogContent className="max-w-lg" data-testid="quick-edit-dialog">
          <DialogHeader>
            <DialogTitle>Quick Edit Session</DialogTitle>
            <DialogDescription>
              Edit key session details directly from the sessions list.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Session Title</Label>
              <Input
                value={quickEditForm.title}
                onChange={(e) => setQuickEditForm(f => ({ ...f, title: e.target.value }))}
                data-testid="quick-edit-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Session Date</Label>
                <Input
                  type="date"
                  value={quickEditForm.session_date}
                  onChange={(e) => setQuickEditForm(f => ({ ...f, session_date: e.target.value }))}
                  data-testid="quick-edit-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={quickEditForm.status}
                  onValueChange={(v) => setQuickEditForm(f => ({ ...f, status: v }))}
                >
                  <SelectTrigger data-testid="quick-edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="border rounded-lg p-3 space-y-3 bg-muted/20">
              <p className="text-sm font-semibold">Trainer Assignment</p>
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Trainer 1</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={quickEditForm.trainer_name}
                      onChange={(e) => setQuickEditForm(f => ({ ...f, trainer_name: e.target.value }))}
                      placeholder="Trainer name"
                      data-testid="quick-edit-trainer-1-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={quickEditForm.trainer_email}
                      onChange={(e) => setQuickEditForm(f => ({ ...f, trainer_email: e.target.value }))}
                      placeholder="trainer@example.com"
                      data-testid="quick-edit-trainer-1-email"
                    />
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground font-medium">Trainer 2 <span className="font-normal">(optional)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={quickEditForm.trainer_2_name}
                      onChange={(e) => setQuickEditForm(f => ({ ...f, trainer_2_name: e.target.value }))}
                      placeholder="Trainer name"
                      data-testid="quick-edit-trainer-2-name"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={quickEditForm.trainer_2_email}
                      onChange={(e) => setQuickEditForm(f => ({ ...f, trainer_2_email: e.target.value }))}
                      placeholder="trainer@example.com"
                      data-testid="quick-edit-trainer-2-email"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setQuickEditOpen(false)}>Cancel</Button>
              <Button
                onClick={handleQuickEditSave}
                disabled={quickEditSaving}
                className="bg-lime text-lime-foreground hover:bg-lime/90"
                data-testid="quick-edit-save-btn"
              >
                {quickEditSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Status Edit Dialog */}
      <Dialog open={bulkEditOpen} onOpenChange={setBulkEditOpen}>
        <DialogContent className="max-w-sm" data-testid="bulk-edit-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Status Update</DialogTitle>
            <DialogDescription>
              Update status for {selectedIds.size} selected session{selectedIds.size > 1 ? 's' : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>New Status</Label>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger data-testid="bulk-status-select">
                  <SelectValue placeholder="Select status..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setBulkEditOpen(false)}>Cancel</Button>
              <Button
                onClick={handleBulkStatusUpdate}
                disabled={!bulkStatus || bulkSaving}
                className="bg-lime text-lime-foreground hover:bg-lime/90"
                data-testid="bulk-status-save-btn"
              >
                {bulkSaving ? 'Updating...' : 'Update Status'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent data-testid="delete-session-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{sessionToDelete?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSessionToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-session-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AllSessionsPage;
